import {TestSession, TestInfo} from '../../../../../../types';
import {useDiagramData} from '../../hooks/use-diagram-data';
import {GridBox} from '@esgi/ui/layout';
import {
	barLineWidth,
	diagramHeight,
	pageSize,
	xAxisDiagramGap,
	yAxisDiagramGap,
	yAxisGap,
	yAxisTicksTitleGap,
	yAxixLineChartWidth,
} from './constants';
import {Notes} from './components/notes';
import {Bar, ComposedChart, Label, Line, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import {StackBarName, XAxisId, YAxisId} from './types';
import {BarItemTooltip} from './components/bar-item-tooltip';
import {BarRect} from './components/bar-rect';
import {getThemeColor} from '@esgi/ui';
import {isNull} from 'underscore';
import {BarBackgroundRect} from './components/bar-background-rect';
import {Pagination} from '../../../../../../kit';
import {EmptyDataMessage} from '../empty-data-message';
import {useVisibleData} from '../../hooks/use-visible-data';

type Props = {
	sessions: TestSession[];
	/**
	 * Prop is uses for class entity. If emptySessionsCount is number, sessions count will be increase on passed value.
	 */
	emptySessionsCount?: number;
	testInfo: TestInfo;
	YAxisLabel: string;
};

export function DefaultDiagram({sessions, testInfo, YAxisLabel, emptySessionsCount = 0}: Props) {
	const {data, sessionsCount} = useDiagramData({sessions, testInfo, emptySessionsCount});

	const {visibleData, currentPageIndex, totalPages, nextPage, previousPage, sessionNote} = useVisibleData({
		data,
		pageSize,
		sessions,
	});

	const YAxisTicksCount = sessionsCount + 1;

	if (!data.length) {
		return <EmptyDataMessage />;
	}

	return (
		<GridBox gap='4'>
			<ResponsiveContainer height={diagramHeight}>
				<ComposedChart
					data={visibleData}
					margin={{top: 24, right: 0, bottom: 0, left: 0}}
					barCategoryGap={5}
					barGap={5}
				>
					<YAxis
						yAxisId={YAxisId.YBarChart}
						tickCount={YAxisTicksCount}
						domain={[0, sessionsCount]}
						interval='preserveStartEnd'
						tickSize={0}
						tickMargin={0}
						tick={{
							fontSize: 10,
							fill: getThemeColor('primary64'),
						}}
						axisLine={false}
					>
						<Label
							position='insideTopRight'
							value={YAxisLabel}
							offset={0}
							dy={-yAxisTicksTitleGap}
							fontSize={10}
							fill={getThemeColor('primary64')}
						/>
					</YAxis>

					<YAxis
						yAxisId={YAxisId.YLineChart}
						tickCount={YAxisTicksCount}
						domain={[0, 'auto']}
						interval='preserveStartEnd'
						tickSize={0}
						tickMargin={0}
						tick={{
							fontSize: 10,
							fill: getThemeColor('secondary64'),
						}}
						axisLine={false}
						width={yAxixLineChartWidth}
						dx={-yAxisGap}
						// @TODO: should be removed, when should be implemented AVG time diagram
						hide
					>
						<Label
							position='insideTopRight'
							value='Sec'
							offset={0}
							dy={-yAxisTicksTitleGap}
							dx={-yAxisGap}
							fontSize={10}
							fill={getThemeColor('secondary64')}
						/>
					</YAxis>

					<XAxis
						xAxisId={XAxisId.XBarChart}
						tickLine={false}
						tickSize={0}
						tickMargin={xAxisDiagramGap}
						axisLine={false}
						tickFormatter={(_, index) =>
							isNull(visibleData[index]) ? '' : String(currentPageIndex * pageSize + index + 1)
						}
						padding={{
							right: barLineWidth / 2,
							left: barLineWidth + yAxisDiagramGap,
						}}
						scale='point'
						tick={{
							fontSize: 10,
							fill: getThemeColor('neutral64'),
						}}
					>
						<Label position='left' value='Question' offset={0} dy={5} fontSize={10} fill={getThemeColor('neutral64')} />
					</XAxis>

					<Bar
						yAxisId={YAxisId.YBarChart}
						xAxisId={XAxisId.XBarChart}
						stackId={StackBarName.AnswerInfo}
						dataKey='correct'
						barSize={barLineWidth}
						fill={getThemeColor('green88')}
						strokeWidth={1}
						stroke={getThemeColor('green88')}
						background={<BarBackgroundRect />}
						shape={<BarRect itemType='correct' borderTopColor='green48' />}
						activeBar={false}
					/>

					<Bar
						yAxisId={YAxisId.YBarChart}
						xAxisId={XAxisId.XBarChart}
						stackId={StackBarName.AnswerInfo}
						dataKey='incorrect'
						barSize={barLineWidth}
						fill={getThemeColor('neutral88')}
						strokeWidth={1}
						stroke={getThemeColor('neutral88')}
						shape={<BarRect itemType='correct' borderTopColor='neutral48' />}
						activeBar={false}
					/>

					<Line
						yAxisId={YAxisId.YLineChart}
						xAxisId={XAxisId.XBarChart}
						type='linear'
						dataKey='sessionTimeInSec'
						stroke={getThemeColor('secondary64')}
						strokeWidth={1.5}
						dot={{
							stroke: getThemeColor('secondary64'),
							strokeWidth: 1.5,
							fill: getThemeColor('secondary96'),
						}}
						// @TODO: should be update by prop, which toggle AVG time diagram
						hide
					/>

					<Tooltip cursor={false} content={<BarItemTooltip sessionsCount={sessionsCount} />} />
				</ComposedChart>
			</ResponsiveContainer>
			{totalPages > 1 && (
				<GridBox justify='end'>
					<Pagination
						currentPageIndex={currentPageIndex}
						nextPage={nextPage}
						previousPage={previousPage}
						totalPages={totalPages}
					/>
				</GridBox>
			)}
			{sessionNote && <Notes sessionNote={sessionNote} contentPaddingLeft={32} />}
		</GridBox>
	);
}
